import { z } from "zod";
import { BusinessDate, EntityBase, Note } from "./common.js";
import { Id } from "./ids.js";
import { Rial } from "./money.js";

/**
 * Price contracts (architecture §7.3, ADR-07, DL-033/034). There is no "contract" entity: only dated rules per
 * portion. Resolution is specific-first; see `PRICE_LEVELS`.
 */

/**
 * What a rule is attached to, most specific first. Organization levels walk up the tree, nearest ancestor first,
 * before falling to the next level (AS-022).
 */
export const PriceTarget = z.discriminatedUnion("level", [
  z.object({ level: z.literal("unit-item"), unitId: Id, menuItemId: Id }),
  z.object({ level: z.literal("organization-item"), organizationId: Id, menuItemId: Id }),
  /** The organization's per-portion price for a tier, whatever the food (the Excel pattern: one price per org). */
  z.object({ level: z.literal("organization-default"), organizationId: Id }),
  z.object({ level: z.literal("item-base"), menuItemId: Id }),
]);
export type PriceTarget = z.infer<typeof PriceTarget>;

export const PRICE_LEVELS = ["unit-item", "organization-item", "organization-default", "item-base"] as const;
export type PriceLevel = (typeof PRICE_LEVELS)[number];

/** A dated price rule. Within one level and target, the latest `validFrom` that covers the date wins. */
export const PriceRule = EntityBase.extend({
  target: PriceTarget,
  /** Null only for companion items, which have no tier. */
  tierId: Id.nullable(),
  amount: Rial,
  validFrom: BusinessDate,
  /** Inclusive last day; null = open-ended. */
  validTo: BusinessDate.nullable(),
  note: Note,
});
export type PriceRule = z.infer<typeof PriceRule>;

export const PriceRuleInput = PriceRule.pick({
  target: true,
  tierId: true,
  amount: true,
  validFrom: true,
  validTo: true,
  note: true,
}).refine((r) => r.validTo === null || r.validTo >= r.validFrom, {
  message: "validTo must not be before validFrom",
  path: ["validTo"],
});
export type PriceRuleInput = z.infer<typeof PriceRuleInput>;

/** Ask the engine for one portion's price. */
export const PriceQuery = z.object({
  unitId: Id,
  menuItemId: Id,
  tierId: Id.nullable(),
  businessDate: BusinessDate,
});
export type PriceQuery = z.infer<typeof PriceQuery>;

/** Where an applied price came from, stored on the order line (`price_source`, §7.3). */
export const PriceSource = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("rule"), ruleId: Id, level: z.enum(PRICE_LEVELS) }),
  /** Set by a manager on the line (bucket/own container, events, retail; DL-037, DL-038). */
  z.object({ kind: z.literal("manual"), reason: Note.min(1), byId: Id }),
  /** No rule matched: the line is valid but unpriced and shows on the manager's exceptions list. */
  z.object({ kind: z.literal("none") }),
]);
export type PriceSource = z.infer<typeof PriceSource>;

export const PriceQuote = z.object({
  amount: Rial.nullable(),
  source: PriceSource,
});
export type PriceQuote = z.infer<typeof PriceQuote>;

/**
 * Re-pricing (§7.3, DL-034): filter → preview → manager approval → apply with audit. Draft invoices recompute;
 * issued invoices get a new revision (F7). The preview is computed, never stored.
 */
export const RepriceFilter = z.object({
  organizationId: Id.nullable(),
  from: BusinessDate,
  to: BusinessDate,
  menuItemId: Id.nullable(),
  tierId: Id.nullable(),
});
export type RepriceFilter = z.infer<typeof RepriceFilter>;

export const RepriceChange = z.discriminatedUnion("kind", [
  /** Re-resolve every line with the current rules (after adding or fixing a rule). */
  z.object({ kind: z.literal("re-resolve") }),
  /** Multiply by permille: 1100 = +10 %. Result rounded to the nearest rial. */
  z.object({ kind: z.literal("factor"), permille: z.int().min(1).max(10_000) }),
  z.object({ kind: z.literal("set-amount"), amount: Rial }),
]);
export type RepriceChange = z.infer<typeof RepriceChange>;

export const RepricePreview = z.object({
  lines: z.int().nonnegative(),
  orders: z.int().nonnegative(),
  /** Sum of (new − old) in rial; may be negative, so a plain integer, not `Rial`. */
  totalDelta: z.int(),
  /** Lines whose price was set by hand are listed, not changed, unless the manager includes them. */
  manualLinesSkipped: z.int().nonnegative(),
});
export type RepricePreview = z.infer<typeof RepricePreview>;
